import { Injectable } from '@angular/core';
import { 
  fetchAuthSession, 
  signInWithRedirect, 
  signOut, 
  getCurrentUser,
  AuthSession,
  JWT 
} from 'aws-amplify/auth';
import { from, Observable, map, catchError, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * Fetches the current auth session
   * @returns Observable<AuthSession>
   */
  getAuthSession(): Observable<AuthSession> {
    return from(fetchAuthSession());
  }

  /**
   * Signs in user with Azure AD provider via redirect
   */
  signIn(): void {
    signInWithRedirect({ provider: { custom: 'DraexlmaierAzureAD' } });
  }

  /**
   * Signs out the current user
   * @returns Promise<void>
   */
  signOut(): Promise<void> {
    return signOut();
  }

  /**
   * Gets the current authenticated user
   * @returns Promise with current user information
   */
  getCurrentUser(): Promise<any> {
    return getCurrentUser();
  }

  /**
   * Gets the ID token from the current session
   * @returns Observable<JWT | undefined | null>
   */
  getIdToken(): Observable<JWT | undefined | null> {
    return from(fetchAuthSession()).pipe(
      map((session: any) => {
        return session.tokens?.idToken;
      })
    );
  }

  /**
   * Gets the user's role(s) from the ID token
   * @returns Observable<string | string[] | null>
   */
  getUserRole(): Observable<string | string[] | null> {
    return from(fetchAuthSession()).pipe(
      map((session: any) => {
        const idToken = session.tokens?.idToken;
        const roles = idToken?.payload?.['custom:role'];
        
        if (!roles) {
          return null;
        }

        // Handle string representation of array: "[Role1, Role2]"
        if (typeof roles === 'string') {
          // Check if it's a string that looks like an array
          if (roles.startsWith('[') && roles.endsWith(']')) {
            // Remove brackets and split by comma
            const rolesStr = roles.slice(1, -1);
            const parsedRoles = rolesStr.split(',').map(r => r.trim()).filter(Boolean);
            return parsedRoles.length > 1 ? parsedRoles : parsedRoles[0] || null;
          }
          // Handle comma-separated string
          const parsedRoles = roles.split(',').map(r => r.trim()).filter(Boolean);
          return parsedRoles.length > 1 ? parsedRoles : roles;
        }

        // Handle actual array of roles
        if (Array.isArray(roles)) {
          const parsedRoles = roles
            .flatMap((role: string) => role.split(',').map(r => r.trim()))
            .filter(Boolean);
          return parsedRoles.length > 1 ? parsedRoles : parsedRoles[0] || null;
        }

        return roles;
      }),
      catchError((error) => {
        console.error('Error fetching role:', error);
        return of(null);
      })
    );
  }
}
